import React, { useRef, useState } from 'react'
import { api } from '../api'
import { Category, TradingGood } from '../types'

interface Props {
  tradingGoods: TradingGood[]
  setTradingGoods?: React.Dispatch<React.SetStateAction<TradingGood[]>>
  categories: Category[]
  onRefresh: () => Promise<void> | void
}

export function TradingGoodsView({ tradingGoods, setTradingGoods, categories, onRefresh }: Props) {
  const [showAddModal, setShowAddModal] = useState(false)
  const [editingItem, setEditingItem] = useState<TradingGood | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [isProcessing, setIsProcessing] = useState(false)

  const [localHiddenIds, setLocalHiddenIds] = useState<Set<string>>(new Set())

  // Search and Category Filter
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedCategory, setSelectedCategory] = useState('')

  const filteredTradingGoods = React.useMemo(() => {
    return tradingGoods.filter((item) => {
      if (localHiddenIds.has(item.id)) return false
      const term = searchTerm.trim().toLowerCase()
      const matchSearch =
        !term ||
        (item.name && item.name.toLowerCase().includes(term)) ||
        (item.code && item.code.toLowerCase().includes(term))
      const matchCategory = !selectedCategory || item.category_id === selectedCategory
      return matchSearch && matchCategory
    })
  }, [tradingGoods, searchTerm, selectedCategory, localHiddenIds])

  // Add Form
  const [code, setCode] = useState('')
  const [name, setName] = useState('')
  const [categoryId, setCategoryId] = useState('')
  const [image, setImage] = useState('')
  const [unit, setUnit] = useState('Adet')
  const [barcode, setBarcode] = useState('')
  const [kdvRate, setKdvRate] = useState('0.20')
  const [maliyet, setMaliyet] = useState('')
  const [methodOverride, setMethodOverride] = useState('')

  // Edit Form
  const [editCode, setEditCode] = useState('')
  const [editName, setEditName] = useState('')
  const [editCategoryId, setEditCategoryId] = useState('')
  const [editImage, setEditImage] = useState('')
  const [editUnit, setEditUnit] = useState('Adet')
  const [editBarcode, setEditBarcode] = useState('')
  const [editKdvRate, setEditKdvRate] = useState('0.20')
  const [editMaliyet, setEditMaliyet] = useState('')
  const [editMethodOverride, setEditMethodOverride] = useState('')

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    try {
      const created = await api.createTradingGood({
        code,
        name,
        category_id: categoryId || null,
        image: image || null,
        unit,
        barcode: barcode || null,
        kdv_rate: parseFloat(kdvRate) || 0.20,
        maliyet: parseFloat(maliyet) || 0,
        calculation_method_override: methodOverride ? (methodOverride as any) : null,
      })
      if (setTradingGoods) {
        setTradingGoods((prev) => [created, ...prev])
      } else {
        await onRefresh()
      }
      setShowAddModal(false)
      setCode('')
      setName('')
      setImage('')
      setUnit('Adet')
      setBarcode('')
      setKdvRate('0.20')
      setMaliyet('')
      setMethodOverride('')
    } catch (err: any) {
      setError(err.message)
    }
  }

  const openEditModal = (item: TradingGood) => {
    setEditingItem(item)
    setEditCode(item.code)
    setEditName(item.name)
    setEditCategoryId(item.category_id || '')
    setEditImage(item.image || '')
    setEditUnit(item.unit || 'Adet')
    setEditBarcode(item.barcode || '')
    setEditKdvRate(String(item.kdv_rate ?? 0.20))
    setEditMaliyet(String(item.maliyet))
    setEditMethodOverride(item.calculation_method_override || '')
  }

  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!editingItem) return
    setError(null)
    try {
      const updated = await api.updateTradingGood(editingItem.id, {
        code: editCode,
        name: editName,
        category_id: editCategoryId || null,
        image: editImage || null,
        unit: editUnit,
        barcode: editBarcode || null,
        kdv_rate: parseFloat(editKdvRate) || 0.20,
        maliyet: parseFloat(editMaliyet) || 0,
        calculation_method_override: editMethodOverride ? (editMethodOverride as any) : null,
      })
      setEditingItem(null)
      if (setTradingGoods) {
        setTradingGoods((prev) => prev.map((item) => (item.id === updated.id ? updated : item)))
      } else {
        await onRefresh()
      }
    } catch (err: any) {
      setError(err.message)
    }
  }

  const handleDelete = async (id: string) => {
    if (!confirm('Bu Al-Sat ürününü silmek istediğinize emin misiniz?')) return
    const itemToDelete = tradingGoods.find((item) => item.id === id)
    const originalIndex = tradingGoods.findIndex((item) => item.id === id)

    if (setTradingGoods) {
      setTradingGoods((prev) => prev.filter((item) => item.id !== id))
    }
    setLocalHiddenIds((prev) => new Set(prev).add(id))

    try {
      await api.deleteTradingGood(id)
    } catch (err: any) {
      if (setTradingGoods && itemToDelete) {
        setTradingGoods((prev) => {
          if (prev.some((item) => item.id === id)) return prev
          const next = [...prev]
          next.splice(originalIndex >= 0 ? originalIndex : 0, 0, itemToDelete)
          return next
        })
      }
      setLocalHiddenIds((prev) => {
        const next = new Set(prev)
        next.delete(id)
        return next
      })
      alert(err.message)
      return
    }

    setLocalHiddenIds((prev) => {
      const next = new Set(prev)
      next.delete(id)
      return next
    })
  }
  const fileInputRef = useRef<HTMLInputElement>(null)

  const handleExportExcel = async () => {
    setIsProcessing(true)
    try {
      await api.exportTradingGoodsExcel()
    } catch (err: any) {
      alert(`Excel indirme hatası: ${err.message}`)
    } finally {
      setIsProcessing(false)
    }
  }

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    const confirmed = window.confirm(
      'Excel dosyasındaki Al-Sat ürünleri sisteme aktarılacaktır.\n\n' +
      '• Eşleşen ürünler güncellenecek, yeni ürünler eklenecektir.\n' +
      '• Excel dosyasında yer almayan mevcut Al-Sat ürünleri silinmeyecektir (korunacaktır).\n\n' +
      'Devam etmek istiyor musunuz?'
    )
    if (!confirmed) {
      if (fileInputRef.current) fileInputRef.current.value = ''
      return
    }

    setIsProcessing(true)
    setError(null)
    try {
      const res = await api.importTradingGoodsExcel(file)
      alert(
        `Excel içe aktarma başarılı!\n\n` +
        `Yeni Eklenen: ${res.created}\n` +
        `Güncellenen: ${res.updated}\n` +
        `Silinen: ${res.deleted}\n` +
        `Toplam İşlenen: ${res.total_processed}`
      )
      await onRefresh()
    } catch (err: any) {
      setError(`Excel yükleme hatası: ${err.message}`)
    } finally {
      setIsProcessing(false)
      if (fileInputRef.current) fileInputRef.current.value = ''
    }
  }

  return (
    <div className="view-container">
      <div className="section-header-flex">
        <div>
          <h2>Al-Sat Ürünler</h2>
        </div>
        <div className="header-actions">
          <input
            type="file"
            ref={fileInputRef}
            style={{ display: 'none' }}
            accept=".xlsx,.xls"
            onChange={handleFileChange}
          />
          <button
            className="btn btn-secondary"
            onClick={() => fileInputRef.current?.click()}
            disabled={isProcessing}
            title="Excel dosyasından içe aktar (tam liste değişimi)"
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="17 8 12 3 7 8" />
              <line x1="12" y1="3" x2="12" y2="15" />
            </svg>
            <span>Excel Yükle</span>
          </button>
          <button
            className="btn btn-secondary"
            onClick={handleExportExcel}
            disabled={isProcessing || tradingGoods.length === 0}
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="7 10 12 15 17 10" />
              <line x1="12" y1="15" x2="12" y2="3" />
            </svg>
            <span>Excel İndir</span>
          </button>
          <button className="btn btn-primary" onClick={() => setShowAddModal(true)}>
            + Yeni Al-Sat Ürün
          </button>
        </div>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}

      {/* Filter Bar */}
      <div className="filter-bar" style={{ display: 'flex', gap: '0.75rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
        <input
          type="text"
          className="form-control"
          placeholder="Al-Sat ürün adı veya koduna göre ara…"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{ maxWidth: '300px' }}
        />
        <select
          className="form-control"
          value={selectedCategory}
          onChange={(e) => setSelectedCategory(e.target.value)}
          style={{ maxWidth: '220px' }}
        >
          <option value="">Tüm Kategoriler</option>
          {categories.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>
      </div>

      <div className="table-card">
        {filteredTradingGoods.length === 0 ? (
          <div className="empty-state">
            <p>{tradingGoods.length === 0 ? 'Kayıtlı Al-Sat ürünü bulunmuyor.' : 'Arama kriterlerine uygun Al-Sat ürünü bulunamadı.'}</p>
          </div>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>KOD</th>
                <th>ÜRÜN ADI</th>
                <th>KATEGORİ</th>
                <th>BİRİM</th>
                <th>ALIŞ MALİYETİ</th>
                <th>KDV</th>
                <th>İŞLEMLER</th>
              </tr>
            </thead>
            <tbody>
              {filteredTradingGoods.map((item) => (
                <tr key={item.id}>
                  <td><strong>{item.code}</strong></td>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      {item.image && (
                        <img
                          src={item.image}
                          alt=""
                          style={{ width: '28px', height: '28px', objectFit: 'cover', borderRadius: '4px' }}
                        />
                      )}
                      <span>{item.name}</span>
                    </div>
                  </td>
                  <td>{item.category?.name || <span className="text-muted">—</span>}</td>
                  <td><span className="badge badge-secondary">{item.unit || 'Adet'}</span></td>
                  <td>₺{Number(item.maliyet).toFixed(2)}</td>
                  <td>%{Number(item.kdv_rate ?? 0.20) * 100}</td>
                  <td>
                    <div className="btn-group">
                      <button className="btn btn-sm btn-secondary" onClick={() => openEditModal(item)}>
                        Düzenle
                      </button>
                      <button className="btn btn-sm btn-danger" onClick={() => handleDelete(item.id)}>
                        Sil
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Add Modal */}
      {showAddModal && (
        <div className="modal-backdrop">
          <div className="modal-box">
            <div className="modal-header">
              <h3>Yeni Al-Sat Ürün Ekle</h3>
              <button className="modal-close" onClick={() => setShowAddModal(false)}>✕</button>
            </div>
            <form onSubmit={handleCreate}>
              <div className="modal-body">
                <div className="form-row">
                  <div className="form-group">
                    <label>Ürün Kodu *</label>
                    <input required value={code} onChange={(e) => setCode(e.target.value)} placeholder="Örn: ALSAT-001" />
                  </div>
                  <div className="form-group">
                    <label>Ürün Adı *</label>
                    <input required value={name} onChange={(e) => setName(e.target.value)} placeholder="Örn: 65W Hızlı Şarj Adaptörü" />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Kategori</label>
                    <select value={categoryId} onChange={(e) => setCategoryId(e.target.value)}>
                      <option value="">Kategori Seçin...</option>
                      {categories.map((c) => (
                        <option key={c.id} value={c.id}>{c.name}</option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Ölçü Birimi</label>
                    <select value={unit} onChange={(e) => setUnit(e.target.value)}>
                      <option value="Adet">Adet</option>
                      <option value="Kg">Kg</option>
                      <option value="Lt">Lt</option>
                    </select>
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Alış Maliyeti (₺) *</label>
                    <input
                      type="number"
                      step="0.01"
                      required
                      value={maliyet}
                      onChange={(e) => setMaliyet(e.target.value)}
                      placeholder="0.00"
                    />
                  </div>
                  <div className="form-group">
                    <label>KDV Oranı</label>
                    <select value={kdvRate} onChange={(e) => setKdvRate(e.target.value)}>
                      <option value="0.20">%20</option>
                      <option value="0.10">%10</option>
                      <option value="0.01">%1</option>
                      <option value="0.00">%0</option>
                    </select>
                  </div>
                </div>


                <div className="form-row">
                  <div className="form-group">
                    <label>Barkod</label>
                    <input value={barcode} onChange={(e) => setBarcode(e.target.value)} placeholder="8690..." />
                  </div>
                  <div className="form-group">
                    <label>Görsel URL</label>
                    <input value={image} onChange={(e) => setImage(e.target.value)} placeholder="https://..." />
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowAddModal(false)}>İptal</button>
                <button type="submit" className="btn btn-primary">Kaydet</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {editingItem && (
        <div className="modal-backdrop">
          <div className="modal-box">
            <div className="modal-header">
              <h3>Al-Sat Ürününü Düzenle</h3>
              <button className="modal-close" onClick={() => setEditingItem(null)}>✕</button>
            </div>
            <form onSubmit={handleUpdate}>
              <div className="modal-body">
                <div className="form-row">
                  <div className="form-group">
                    <label>Ürün Kodu *</label>
                    <input required value={editCode} onChange={(e) => setEditCode(e.target.value)} />
                  </div>
                  <div className="form-group">
                    <label>Ürün Adı *</label>
                    <input required value={editName} onChange={(e) => setEditName(e.target.value)} />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Kategori</label>
                    <select value={editCategoryId} onChange={(e) => setEditCategoryId(e.target.value)}>
                      <option value="">Kategori Seçin...</option>
                      {categories.map((c) => (
                        <option key={c.id} value={c.id}>{c.name}</option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Ölçü Birimi</label>
                    <select value={editUnit} onChange={(e) => setEditUnit(e.target.value)}>
                      <option value="Adet">Adet</option>
                      <option value="Kg">Kg</option>
                      <option value="Lt">Lt</option>
                    </select>
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Alış Maliyeti (₺) *</label>
                    <input
                      type="number"
                      step="0.01"
                      required
                      value={editMaliyet}
                      onChange={(e) => setEditMaliyet(e.target.value)}
                    />
                  </div>
                  <div className="form-group">
                    <label>KDV Oranı</label>
                    <select value={editKdvRate} onChange={(e) => setEditKdvRate(e.target.value)}>
                      <option value="0.20">%20</option>
                      <option value="0.10">%10</option>
                      <option value="0.01">%1</option>
                      <option value="0.00">%0</option>
                    </select>
                  </div>
                </div>


                <div className="form-row">
                  <div className="form-group">
                    <label>Barkod</label>
                    <input value={editBarcode} onChange={(e) => setEditBarcode(e.target.value)} />
                  </div>
                  <div className="form-group">
                    <label>Görsel URL</label>
                    <input value={editImage} onChange={(e) => setEditImage(e.target.value)} />
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setEditingItem(null)}>İptal</button>
                <button type="submit" className="btn btn-primary">Güncelle</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}
